export type TextureMetadata = {
	id: string;
	title: string;
	url: string;
	thumbnailUrl: string;
	model?: string;
	style?: 'realistic' | 'stylized';
};

export type MaterialMetadata = {
	id: string;
	title: string;
	url: string;
	thumbnailUrl: string;
};

type TextureInput = Omit<TextureMetadata, 'id'>;
type MaterialInput = Omit<MaterialMetadata, 'id'>;

// Simple hash function that works in browser
function hashString(str: string): string {
	let h1 = 0xdeadbeef;
	let h2 = 0x41c6ce57;
	for (let i = 0; i < str.length; i++) {
		const ch = str.charCodeAt(i);
		h1 = Math.imul(h1 ^ ch, 2654435761);
		h2 = Math.imul(h2 ^ ch, 1597334677);
	}
	h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507) ^ Math.imul(h2 ^ (h2 >>> 13), 3266489909);
	h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507) ^ Math.imul(h1 ^ (h1 >>> 13), 3266489909);
	const hash1 = (h1 >>> 0).toString(16).padStart(8, '0');
	const hash2 = (h2 >>> 0).toString(16).padStart(8, '0');
	// Generate more bits by hashing again with different seeds
	let h3 = 0x12345678;
	let h4 = 0x87654321;
	for (let i = 0; i < str.length; i++) {
		const ch = str.charCodeAt(i);
		h3 = Math.imul(h3 ^ ch, 2246822507);
		h4 = Math.imul(h4 ^ ch, 3266489909);
	}
	h3 = Math.imul(h3 ^ (h3 >>> 16), 2654435761) ^ Math.imul(h4 ^ (h4 >>> 13), 1597334677);
	h4 = Math.imul(h4 ^ (h4 >>> 16), 2654435761) ^ Math.imul(h3 ^ (h3 >>> 13), 1597334677);
	const hash3 = (h3 >>> 0).toString(16).padStart(8, '0');
	const hash4 = (h4 >>> 0).toString(16).padStart(8, '0');
	return hash1 + hash2 + hash3 + hash4;
}

function urlToUUID(url: string): string {
	const hash = hashString(url);
	// Format as UUID: 8-4-4-4-12
	return `${hash.slice(0, 8)}-${hash.slice(8, 12)}-${hash.slice(12, 16)}-${hash.slice(16, 20)}-${hash.slice(20, 32)}`;
}

const texturesInput: TextureInput[] = [
	{
		title: 'Mossy cliff',
		url: 'https://storage.iliad.ai/437f30ad-3133-425f-9180-2c0c3c275209.png',
		thumbnailUrl: 'https://storage.iliad.ai/437f30ad-3133-425f-9180-2c0c3c275209-small.jpg',
		model: 'Aeolian',
		style: 'stylized'
	},
	{
		title: 'Dark hardwood grain',
		url: 'https://storage.iliad.ai/4325ea45-3ef6-40d9-bb28-5fa6bcdf3a3a.png',
		thumbnailUrl: 'https://storage.iliad.ai/4325ea45-3ef6-40d9-bb28-5fa6bcdf3a3a-small.jpg',
		model: 'Aeolian',
		style: 'realistic'
	},
	{
		title: 'Fuji apple',
		url: 'https://storage.iliad.ai/6cfe3ed6-98e0-4a77-8f62-653dc64ca417.png',
		thumbnailUrl: 'https://storage.iliad.ai/6cfe3ed6-98e0-4a77-8f62-653dc64ca417-small.jpg',
		model: 'Aeolian',
		style: 'realistic'
	},
	{
		title: 'Old wooden floor',
		url: 'https://storage.iliad.ai/3754ac3b-631d-49d6-ad2c-a3897b2e823e.png',
		thumbnailUrl: 'https://storage.iliad.ai/3754ac3b-631d-49d6-ad2c-a3897b2e823e-small.jpg',
		model: 'Aeolian',
		style: 'stylized'
	},
	{
		title: 'Spotted fur',
		url: 'https://storage.iliad.ai/89a9d6c2-a02d-4d94-85a9-d56c408bf988.png',
		thumbnailUrl: 'https://storage.iliad.ai/89a9d6c2-a02d-4d94-85a9-d56c408bf988-small.jpg',
		model: 'Aeolian',
		style: 'realistic'
	},
	{
		title: 'Rose petal covering',
		url: 'https://storage.iliad.ai/65be59d5-56ce-4b27-a7f2-8fc3abda2b77.png',
		thumbnailUrl: 'https://storage.iliad.ai/65be59d5-56ce-4b27-a7f2-8fc3abda2b77-small.jpg',
		model: 'Aeolian',
		style: 'realistic'
	},
	{
		title: 'Galvanized metal',
		url: 'https://storage.iliad.ai/68eab58e-0711-4979-af83-e6d60466c227.png',
		thumbnailUrl: 'https://storage.iliad.ai/68eab58e-0711-4979-af83-e6d60466c227-small.jpg',
		model: 'Aeolian',
		style: 'stylized'
	},
	{
		title: 'Vertical worn planks',
		url: 'https://storage.iliad.ai/0a4a501d-fb3d-47b3-9d57-b8b08ea71fc6.png',
		thumbnailUrl: 'https://storage.iliad.ai/0a4a501d-fb3d-47b3-9d57-b8b08ea71fc6-small.jpg',
		model: 'Aeolian',
		style: 'realistic'
	},
	{
		title: 'Grass',
		url: 'https://storage.iliad.ai/b317867d-5402-4f91-bfd3-b62bf03e5b65.png',
		thumbnailUrl: 'https://storage.iliad.ai/b317867d-5402-4f91-bfd3-b62bf03e5b65-small.jpg',
		model: 'Aeolian',
		style: 'stylized'
	},
	{
		title: 'Grass',
		url: 'https://storage.iliad.ai/e5ce0b50-72f9-463a-be84-3ab44da59b8b.png',
		thumbnailUrl: 'https://storage.iliad.ai/e5ce0b50-72f9-463a-be84-3ab44da59b8b-small.jpg',
		model: 'Aeolian',
		style: 'stylized'
	},
	{
		title: 'Grass',
		url: 'https://storage.iliad.ai/059d37ec-6e9d-4e06-bc63-6622dc1275d2.png',
		thumbnailUrl: 'https://storage.iliad.ai/059d37ec-6e9d-4e06-bc63-6622dc1275d2-small.jpg',
		model: 'Aeolian',
		style: 'stylized'
	},
	{
		title: 'Purple grass',
		url: 'https://storage.iliad.ai/3dc7407a-5476-4e42-a78c-a090faa13aa4.png',
		thumbnailUrl: 'https://storage.iliad.ai/3dc7407a-5476-4e42-a78c-a090faa13aa4-small.jpg',
		model: 'Aeolian',
		style: 'stylized'
	},
	{
		title: 'White cliff',
		url: 'https://storage.iliad.ai/409e9f3e-ab15-41ab-a9a6-585e1e93a6e7.png',
		thumbnailUrl: 'https://storage.iliad.ai/409e9f3e-ab15-41ab-a9a6-585e1e93a6e7-small.jpg',
		model: 'Aeolian',
		style: 'stylized'
	},
	{
		title: 'Mossy cliff',
		url: 'https://storage.iliad.ai/5435e461-ff47-4f48-bbe5-8cee2639596f.png',
		thumbnailUrl: 'https://storage.iliad.ai/5435e461-ff47-4f48-bbe5-8cee2639596f-small.jpg',
		model: 'Aeolian',
		style: 'stylized'
	},
	{
		title: 'Dark iridescent liquid',
		url: 'https://storage.iliad.ai/52cc521e-bcb2-414a-8bb6-78172edfe01b.png',
		thumbnailUrl: 'https://storage.iliad.ai/52cc521e-bcb2-414a-8bb6-78172edfe01b-small.jpg',
		model: 'Aeolian',
		style: 'realistic'
	},
	{
		title: 'Patterned ceramic tiles',
		url: 'https://storage.iliad.ai/f444d5cd-b44b-4e27-bb97-bab1871bcadb.png',
		thumbnailUrl: 'https://storage.iliad.ai/f444d5cd-b44b-4e27-bb97-bab1871bcadb-small.jpg',
		model: 'Aeolian',
		style: 'stylized'
	},
	{
		title: 'Ceramic tiles',
		url: 'https://storage.iliad.ai/881cc517-693d-458f-9306-153b6f08f2ca.png',
		thumbnailUrl: 'https://storage.iliad.ai/881cc517-693d-458f-9306-153b6f08f2ca-small.jpg',
		model: 'Aeolian',
		style: 'stylized'
	},
	{
		title: 'Greyscale wood grain',
		url: 'https://storage.iliad.ai/6760cac0-b3d3-4ffa-9d47-5132c0243bd0.png',
		thumbnailUrl: 'https://storage.iliad.ai/6760cac0-b3d3-4ffa-9d47-5132c0243bd0-small.jpg',
		model: 'Aeolian',
		style: 'realistic'
	},
	{
		title: 'Vine pattern',
		url: 'https://storage.iliad.ai/a86548f8-74d0-4ab1-a54f-5c8fa82e76ec.png',
		thumbnailUrl: 'https://storage.iliad.ai/a86548f8-74d0-4ab1-a54f-5c8fa82e76ec-small.jpg',
		model: 'Aeolian',
		style: 'stylized'
	},
	{
		title: 'Wildflower pattern',
		url: 'https://storage.iliad.ai/8605e1d1-d8e6-4b3c-ab94-d2c640461a0b.png',
		thumbnailUrl: 'https://storage.iliad.ai/8605e1d1-d8e6-4b3c-ab94-d2c640461a0b-small.jpg',
		model: 'Aeolian',
		style: 'stylized'
	},
	{
		title: 'Colorful floral pattern',
		url: 'https://storage.iliad.ai/9ae2df09-66fa-4694-a6a7-d6dfa1873a32.png',
		thumbnailUrl: 'https://storage.iliad.ai/9ae2df09-66fa-4694-a6a7-d6dfa1873a32-small.jpg',
		model: 'Aeolian',
		style: 'stylized'
	},
	{
		title: 'Stone brick wall',
		url: 'https://storage.iliad.ai/3f09a614-73c1-4d40-bdc6-dcaab374b110.png',
		thumbnailUrl: 'https://storage.iliad.ai/3f09a614-73c1-4d40-bdc6-dcaab374b110-small.jpg',
		model: 'Aeolian',
		style: 'stylized'
	},
	{
		title: 'Raspberries',
		url: 'https://storage.iliad.ai/a84177af-2d32-48dd-8c91-16a9f0073f8d.png',
		thumbnailUrl: 'https://storage.iliad.ai/a84177af-2d32-48dd-8c91-16a9f0073f8d-small.jpg',
		model: 'GPT',
		style: 'realistic'
	},
	{
		title: 'Water-smoothed stones',
		url: 'https://storage.iliad.ai/77d9f1da-0968-4968-9bd4-f5a41e9d0c4b.png',
		thumbnailUrl: 'https://storage.iliad.ai/77d9f1da-0968-4968-9bd4-f5a41e9d0c4b-small.jpg',
		model: 'Aeolian',
		style: 'stylized'
	},
	{
		title: 'Horizontal birchwood planks with vines',
		url: 'https://storage.iliad.ai/418b8ca4-2684-4659-86f9-4f231f302b42.png',
		thumbnailUrl: 'https://storage.iliad.ai/418b8ca4-2684-4659-86f9-4f231f302b42-small.jpg',
		model: 'Aeolian',
		style: 'stylized'
	},
	{
		title: 'Blue velvet',
		url: 'https://storage.iliad.ai/b768e06f-0a2b-4d96-89fc-910838c3bbe0.png',
		thumbnailUrl: 'https://storage.iliad.ai/b768e06f-0a2b-4d96-89fc-910838c3bbe0-small.jpg',
		model: 'Aeolian',
		style: 'realistic'
	},
	{
		title: 'Red velvet',
		url: 'https://storage.iliad.ai/1457cbb5-53b2-4979-ad2b-e11a93c00b5a.png',
		thumbnailUrl: 'https://storage.iliad.ai/1457cbb5-53b2-4979-ad2b-e11a93c00b5a-small.jpg',
		model: 'Aeolian',
		style: 'realistic'
	},
];

const materialsInput: MaterialInput[] = [];

export const textures: TextureMetadata[] = texturesInput.map((t) => ({
	...t,
	id: urlToUUID(t.url)
}));

export const materials: MaterialMetadata[] = materialsInput.map((m) => ({
	...m,
	id: urlToUUID(m.url)
}));

export function getTextureById(id: string): TextureMetadata | undefined {
	return textures.find((t) => t.id === id);
}

export function getAllTextures(): TextureMetadata[] {
	return textures;
}
